import uniq from 'lodash/uniq'

import { createSelector } from '@app/utils/createSelector'
import { invariant } from '@app/utils/invariant'
import { mergeMaps } from '@app/utils/mergeMaps'
import { mergeV2Entity } from '@app/utils/mergeV2Entity'

import type { ReviewsPageMeta } from '@app/packages/api/v2/actions/reviews'
import type { ApiRequestReview, ApiUser } from '@app/packages/api/v2/types/entities'
import type { GetReviewsQuery } from '@app/packages/api/v2/types/params'

import { createReduxSlice } from '@app/store/redux_slice'
import { createThunk } from '@app/store/thunk'

const reviewsListSlice = createReduxSlice<string[]>('reviews_list')
const reviewsModelsSlice = createReduxSlice<{ [key: string]: ApiRequestReview }>('reviews_models')
const reviewsUsersSlice = createReduxSlice<{ [key: string]: ApiUser }>('reviews_users')
const reviewsMetaSlice = createReduxSlice<ReviewsMeta>('reviews_meta')
const reviewsQuerySlice = createReduxSlice<ReviewsQuery>('reviews_query')

export const addReviewsPageSlice = (
  query: GetReviewsQuery,
  resp: { data: ApiRequestReview[]; included?: { id: string; type: string }[]; meta: ReviewsPageMeta }
) =>
  createThunk(dispatch => {
    const keys = resp.data.map(model => model.id)
    dispatch(reviewsListSlice.update(state => (query.page === 1 ? keys : uniq((state ?? EMPTY_LIST).concat(keys)))))
    dispatch(
      reviewsModelsSlice.update(state => ({
        ...state,
        ...resp.data.reduce<{ [key: string]: ApiRequestReview }>((c, model) => {
          c[model.id] = mergeV2Entity(state?.[model.id], model)
          return c
        }, {}),
      }))
    )
    dispatch(
      reviewsUsersSlice.update(state =>
        mergeMaps(
          state ?? {},
          (resp.included ?? []).reduce<{ [key: string]: ApiUser }>((acc, e) => {
            if (e.type === 'users') acc[e.id] = e as ApiUser
            return acc
          }, {})
        )
      )
    )
    dispatch(reviewsMetaSlice.update(state => ({ ...(state ?? DEFAULT_META), ...resp.meta, per_page: query.per_page, loading: false })))
    dispatch(
      reviewsQuerySlice.set({
        page: resp.meta.current_page,
        per_page: query.per_page,
        token: query.token ?? invariant('Token is missing'),
      })
    )
  })

export const startReviewsPageSlice = () => reviewsMetaSlice.update(state => ({ ...(state ?? DEFAULT_META), loading: true }))

export const failReviewsPageSlice = () => reviewsMetaSlice.update(state => ({ ...(state ?? DEFAULT_META), loading: false }))

export const reviewsModelsSelector = createSelector([reviewsModelsSlice.selector], models => models ?? {})
export const reviewsListSelector = createSelector([reviewsListSlice.selector], list => list ?? EMPTY_LIST)
export const reviewsUsersSelector = createSelector([reviewsUsersSlice.selector], users => users ?? {})
export const reviewsMetaSelector = createSelector([reviewsMetaSlice.selector], meta => meta ?? DEFAULT_META)
export const reviewsQuerySelector = createSelector([reviewsQuerySlice.selector], query => query ?? DEFAULT_QUERY)

export const reviewsSelector = createSelector([reviewsListSelector, reviewsModelsSelector, reviewsUsersSelector], (list, models, users) => {
  return list.map(id => {
    const review = models[id]
    const reviewerId = review.relationships.reviewer.data!.id
    const reviewer = users[reviewerId]

    const revieweeId = review.relationships.reviewee.data!.id
    const reviewee = users[revieweeId]

    return { review, reviewee, reviewer }
  })
})

type ReviewsMeta = { loading: boolean; current_page: number; total: number; total_pages: number; per_page: number }

type ReviewsQuery = { page: number; per_page: number; token: string }

const EMPTY_LIST: string[] = []

const DEFAULT_META: ReviewsMeta = {
  loading: false,
  current_page: 0,
  total: 0,
  total_pages: 0,
  per_page: 10,
}

const DEFAULT_QUERY: ReviewsQuery = {
  page: 0,
  per_page: 10,
  token: '',
}
